import React from 'react';
import { CaretLeft, CaretRight, Lock } from '@phosphor-icons/react';
import { DebitCardWithTruck } from '../../../../../../../types/apiTypes';
import { SwipeableHandlers } from 'react-swipeable';
import ClipLoader from 'react-spinners/ClipLoader';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';

interface CardDisplayProps {
  cards: DebitCardWithTruck[];
  currCard: DebitCardWithTruck | null;
  currCardIdx: number;
  currCardImage: string | null;
  hideCardDetails: boolean;
  userRecord: any;
  handleImageError: () => Promise<void>;
  fetchCardImage: (debit_card_id: string) => Promise<void>;
  setCurrCardImage: (image: string | null) => void;
  setHideCardDetails: (hide: boolean) => void;
  setCurrCardIdx: (idx: number) => void;
  swipeHandlers: SwipeableHandlers;
  cardImageLoading: boolean;
  slideDirection: 'left' | 'right' | null;
  isTransitioning: boolean;
  nextCardIdx: number | null;
  setSlideDirection: (direction: 'left' | 'right' | null) => void;
  setIsTransitioning: (isTransitioning: boolean) => void;
  setNextCardIdx: (idx: number | null) => void;
  onArrowNavigate?: (nextIdx: number) => void;
}

const CardDisplay = ({
  cards,
  currCard,
  currCardIdx,
  currCardImage,
  hideCardDetails,
  userRecord,
  handleImageError,
  fetchCardImage,
  setCurrCardImage,
  setHideCardDetails,
  setCurrCardIdx,
  swipeHandlers,
  cardImageLoading,
  slideDirection,
  isTransitioning,
  nextCardIdx,
  setSlideDirection,
  setIsTransitioning,
  setNextCardIdx,
  onArrowNavigate,
}: CardDisplayProps) => {
  const cardWidth = 320;
  const cardHeight = 200;
  const { screenView } = useScreenView();

  // State for mouse-based 3D tilt effect
  const [rotateX, setRotateX] = React.useState(0);
  const [rotateY, setRotateY] = React.useState(0);
  const [isHovering, setIsHovering] = React.useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isTransitioning || screenView !== 'desktop') return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate center of card
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate rotation angles (max ±15 degrees)
    const rotateYAngle = ((x - centerX) / centerX) * 15;
    const rotateXAngle = ((centerY - y) / centerY) * 15;

    setRotateX(rotateXAngle);
    setRotateY(rotateYAngle);
  };

  const handleMouseEnter = () => {
    if (screenView !== 'desktop') return;
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    if (screenView !== 'desktop') return;
    setIsHovering(false);
    setRotateX(0);
    setRotateY(0);
  };
  return (
    <div
      className={`relative flex w-[${cardWidth}px] !-mt-4 flex-col items-center justify-center`}
    >
      <style>
        {`
          @keyframes slideFromRight {
            0% { transform: translateX(400px); opacity: 0; }
            20% { opacity: 1; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideFromLeft {
            0% { transform: translateX(-400px); opacity: 0; }
            20% { opacity: 1; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideToLeft {
            0% { transform: translateX(0); opacity: 1; }
            80% { opacity: 0; }
            100% { transform: translateX(-400px); opacity: 0; }
          }
          @keyframes slideToRight {
            0% { transform: translateX(0); opacity: 1; }
            80% { opacity: 0; }
            100% { transform: translateX(400px); opacity: 0; }
          }
        `}
      </style>

      {cards && cards?.length > 1 && screenView !== 'mobile' && (
        <CaretLeft
          className="absolute -left-12 top-[45%] z-40 hover:cursor-pointer hover:opacity-60"
          style={{ opacity: 0.7, transition: 'opacity 200ms' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '.7')}
          size={24}
          weight="bold"
          onClick={() => {
            if (!isTransitioning) {
              setIsTransitioning(true);
              setSlideDirection('right');
              setHideCardDetails(true);

              let next = currCardIdx - 1;
              if (next === -1) {
                next = cards?.length - 1;
              }

              setNextCardIdx(next);
              setCurrCardImage(null);
              onArrowNavigate?.(next);

              setTimeout(() => {
                setCurrCardIdx(next);
                setNextCardIdx(null);
                setSlideDirection(null);
                setIsTransitioning(false);
              }, 300);
            }
          }}
        />
      )}

      {cards && cards?.length > 1 && screenView !== 'mobile' && (
        <CaretRight
          className="absolute -right-12 top-[45%] z-40 hover:cursor-pointer hover:opacity-60"
          size={24}
          weight="bold"
          style={{ opacity: 0.7, transition: 'opacity 200ms' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '.7')}
          onClick={() => {
            if (!isTransitioning) {
              setIsTransitioning(true);
              setSlideDirection('left');
              setHideCardDetails(true);

              const next = (currCardIdx + 1) % cards?.length;
              setNextCardIdx(next);
              setCurrCardImage(null);
              onArrowNavigate?.(next);

              setTimeout(() => {
                setCurrCardIdx(next);
                setNextCardIdx(null);
                setSlideDirection(null);
                setIsTransitioning(false);
              }, 300);
            }
          }}
        />
      )}

      {/* Container with perspective for 3D effect */}
      <div className="relative overflow-hidden p-10 transition-all duration-300">
        <div
          className={`relative h-[${cardHeight}px] w-[${cardWidth}px]`}
          style={{ perspective: '1000px' }}
        >
          {/* Subtle background glow */}
          <div
            className="absolute inset-0 -z-10 scale-110 rounded-full bg-gradient-to-br from-blue-400/10 via-purple-400/10 to-pink-400/10 opacity-20 blur-3xl transition-opacity duration-300"
            style={{ opacity: isHovering ? 0.3 : 0.15 }}
          />

          {/* Current Card */}
          <div
            {...swipeHandlers}
            className={`group absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg bg-primary ${
              isTransitioning ? 'transition-all duration-300 ease-in-out' : ''
            } transform-gpu transition-all duration-200 ease-out`}
            style={{
              animation: isTransitioning
                ? `slideTo${slideDirection === 'left' ? 'Left' : 'Right'} 300ms ease-out forwards`
                : undefined,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              zIndex: isTransitioning ? 1 : 2,
              transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${isHovering ? '20px' : '0px'})`,
              transformStyle: 'preserve-3d',
              willChange: 'transform, opacity',
            }}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {cardImageLoading && (
              <div className="absolute inset-0 z-40 flex items-center justify-center rounded-lg bg-black bg-opacity-40">
                <ClipLoader color="#ffffff" size={40} />
              </div>
            )}
            {currCard?.frozen && (
              <div className="absolute bottom-0 left-0 z-30 flex h-full w-full flex-col justify-between">
                <div className="inset-0 flex h-full w-full items-center justify-center rounded-t-lg bg-black bg-opacity-60 align-middle">
                  <div className="flex h-[40px] w-[110px] flex-row items-center justify-center space-x-2 rounded-[4px] border-[1px] border-white border-opacity-[24%] bg-[#25343C] bg-opacity-[40%] p-2 align-middle backdrop-blur-[24%]">
                    <Lock size={24} className="text-white" />{' '}
                    <p className="text-[16px] text-white">Frozen</p>
                  </div>
                </div>
                <div className="flex h-[65px] w-full flex-row items-center justify-between space-x-2 rounded-b-lg border-[1px] border-[#0390F4] bg-[#E6F4FE] px-3 py-2 align-middle">
                  <Lock size={24} className="text-[#0390F4]" weight="bold" />{' '}
                  <p className="text-[14px] text-primary">
                    This card is frozen. You can unfreeze it in the card
                    settings.
                  </p>
                </div>
              </div>
            )}
            {hideCardDetails &&
              !currCard?.frozen &&
              currCard?.debit_card_id && (
                <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-black bg-opacity-60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <button
                    className="flex items-center justify-center space-x-2 border-none text-lg font-semibold text-white"
                    onClick={async () => {
                      await fetchCardImage(currCard.debit_card_id);
                      setHideCardDetails(false);
                    }}
                  >
                    Click to see details
                  </button>
                </div>
              )}
            {!currCardImage ? (
              <div className="relative h-full w-full">
                <img
                  src="/bp-tach-card.webp"
                  alt="card-image"
                  className={`absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg object-cover`}
                  style={{
                    boxShadow: `
                    ${-rotateY * 0.2}px ${rotateX * 0.3}px 4px rgba(0,0,0,0.08),
                    ${-rotateY * 0.5}px ${rotateX * 0.7}px 12px rgba(0,0,0,0.1),
                    ${-rotateY * 0.8}px ${rotateX * 1.2}px 24px rgba(0,0,0,0.12),
                    ${-rotateY * 1}px ${rotateX * 1.5}px 40px rgba(0,0,0,${isHovering ? 0.15 : 0.1})
                  `,
                  }}
                />
                {/* Glossy reflection overlay */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-60"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 25%, transparent 50%, rgba(0,0,0,0.05) 100%)',
                  }}
                />
                {/* Edge highlight */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-lg"
                  style={{
                    boxShadow:
                      'inset 0 1px 2px rgba(255,255,255,0.3), inset 0 -1px 1px rgba(0,0,0,0.2)',
                  }}
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={currCardImage}
                  alt="card-image"
                  className="absolute left-0 top-0 rounded-lg object-cover"
                  style={{
                    boxShadow: `
                    ${-rotateY * 0.2}px ${rotateX * 0.3}px 4px rgba(0,0,0,0.08),
                    ${-rotateY * 0.5}px ${rotateX * 0.7}px 12px rgba(0,0,0,0.1),
                    ${-rotateY * 0.8}px ${rotateX * 1.2}px 24px rgba(0,0,0,0.12),
                    ${-rotateY * 1}px ${rotateX * 1.5}px 40px rgba(0,0,0,${isHovering ? 0.15 : 0.1})
                  `,
                  }}
                  onError={handleImageError}
                />
                {/* Glossy reflection overlay */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-60"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 25%, transparent 50%, rgba(0,0,0,0.05) 100%)',
                  }}
                />
                {/* Edge highlight */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-lg"
                  style={{
                    boxShadow:
                      'inset 0 1px 2px rgba(255,255,255,0.3), inset 0 -1px 1px rgba(0,0,0,0.2)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Next Card (for animation) */}
          {nextCardIdx !== null && cards && (
            <div
              className={`group absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg bg-primary px-6 pb-2 pt-6`}
              style={{
                animation: isTransitioning
                  ? `slideFrom${slideDirection === 'left' ? 'Right' : 'Left'} 300ms ease-out forwards`
                  : undefined,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                zIndex: isTransitioning ? 2 : 1,
                opacity: isTransitioning ? 0 : 1,
                willChange: 'transform, opacity',
              }}
            >
              <div className="h-full w-full hover:cursor-pointer">
                <img
                  src="/bp-tach-card.webp"
                  alt="card-image"
                  className={`absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg object-cover`}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {cards?.length > 1 && (
        <div className="z-[20] -mt-4 flex w-full flex-row items-center justify-center space-x-2 align-middle">
          {cards?.map((card, idx) => {
            return (
              <div
                key={idx}
                className={`h-[8px] w-[8px] rounded-full ${idx === currCardIdx ? 'bg-accent' : 'bg-[#FDEACB]'}`}
              ></div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CardDisplay;
